import { NgSwitch, NgSwitchCase } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  input,
  output,
  Output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LocalizationPipe } from '@shared/modules/localization';
import { NzFormModule } from 'ng-zorro-antd/form';
import { UIWysiwyg } from '../wysiwyg';
import {
  UIFormCheckbox,
  UIFormDate,
  UIFormInput,
  UIFormSelect,
  UIFormTextarea, UIFormTimepicker,
} from './components';
import { UIFormFile } from './components/form-file/form-file.component';
import { UIFormPassword } from './components/form-password/form-password.component';
import { UIFormTime } from './components/form-time/form-time.component';
import { GetFormControlPipe } from './get-form-control.pipe';
import {
  FormFieldType,
  FormGroupDirectionType,
  IFormGroupConfig,
  IFormGroupFieldConfig,
} from './models';

@Component({
  selector: 'ui-form',
  templateUrl: './form.component.html',
  styleUrl: './form.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    UIWysiwyg,
    NgSwitch,
    NgSwitchCase,
    ReactiveFormsModule,
    NzFormModule,
    GetFormControlPipe,
    UIFormInput,
    UIFormSelect,
    UIFormDate,
    UIFormCheckbox,
    UIFormPassword,
    UIFormTextarea,
    UIFormFile,
    LocalizationPipe,
    UIFormTime,
    UIFormTimepicker,
  ],
})
export class UIForm {
  form = input<FormGroup>();
  fieldName = input<string>();

  enterPressed = output<void>();

  protected onEnterPressed(): void {
    this.enterPressed.emit();
  }

  config = input<IFormGroupConfig>();
  parentWidth = input<number>();
  offsetWidth = signal<number>(0);

  hostWidth = computed(() => {
    const pWidth = this.parentWidth();
    if (pWidth) {
      return pWidth;
    }
    return this.offsetWidth();
  });

  formLabel = computed(() => {
    return this.config()?.label ?? '';
  });

  private formValuesChanged = signal(0); // Триггер для пересчета groupFields

  groupFields = computed<[string, NonNullable<any>][]>(() => {
    const rootForm = this.form(); // <-- корневой
    this.formValuesChanged(); // Подписка на триггер изменений

    return Object.entries(this.config()?.fields ?? {})
      .filter(field => !field[1]?.hidden)
      .filter(field => {
        const hideConditions = (field[1] as IFormGroupConfig)?.hideCondition ?? [];

        if (!rootForm || hideConditions.length === 0) return true;

        // Скрыть, только если все условия возвращают true
        const shouldHide = () =>
          hideConditions.every(hideCondition => {
            const fieldControlValue = rootForm?.get(hideCondition.fieldName)?.getRawValue();
            return hideCondition.condition ? hideCondition.condition(fieldControlValue) : false;
          });

        return !shouldHide(); // показываем, если НЕ нужно скрывать
      });
  });

  direction = computed<FormGroupDirectionType>(() => {
    return this.config()?.direction ?? FormGroupDirectionType.COLUMN;
  });

  groupDirection = computed<string>(() => {
    const hostWidth = this.hostWidth();

    return this.direction() === FormGroupDirectionType.ROW
      ? `grid-template-columns: repeat(${
          this.groupFields().length
        }, ${this.childItemWidthPx()}); grid-template-rows: auto;`
      : `grid-template-rows: auto; grid-template-columns: ${
          hostWidth ? hostWidth + 'px' : 'minmax(0, 1fr)'
        };`;
  });

  groupItemGap = computed<string>(() => {
    return this.direction() === FormGroupDirectionType.ROW ? '24px' : '0';
  });

  @HostListener('window:resize')
  onResize() {
    this.initializeElementWidth();
  }

  childItemWidthPx = computed(() => {
    const groupFieldsLength = this.groupFields().length;
    return (
      (groupFieldsLength == 0
        ? this.hostWidth()
        : (this.hostWidth() - this.childItemGapSum()) / groupFieldsLength) + 'px'
    );
  });

  childItemGapSum = computed(() => {
    return ((this.groupFields().length ?? 1) - 1) * 24;
  });

  FormFieldType = FormFieldType;

  private initializeElementWidth() {
    // Set initial value after view init
    setTimeout(() => {
      this.offsetWidth.set(this.el.nativeElement.offsetWidth);
    });
  }

  @Output() linkClicked = new EventEmitter<void>();

  trackByField(_index: number, item: [string, NonNullable<unknown>]) {
    return item[0]; // ключ поля, например 'username', 'email', и т.д.
  }

  constructor(private el: ElementRef, private destroyRef: DestroyRef) {
    this.initializeElementWidth();

    // Отслеживаем изменения формы для реактивного обновления groupFields
    effect(() => {
      const form = this.form();
      const config = this.config();

      if (form && config) {
        this.subscribeToFormChanges(form, config);
      }
    });
  }

  private subscribeToFormChanges(form: FormGroup, config: IFormGroupConfig): void {
    // Собираем все поля, от которых зависят hideCondition
    const fields = config.fields;
    if (!fields) return;

    const watchedFields = this.collectWatchedFields(fields);

    if (watchedFields.length === 0) return;

    // Подписываемся на изменения только нужных полей
    watchedFields.forEach(fieldName => {
      const control = form.get(fieldName);
      if (control) {
        control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(vvv => {
          // Триггерим пересчет groupFields
          this.formValuesChanged.update(v => v + 1);
        });
      }
    });
  }

  private collectWatchedFields(fields: IFormGroupFieldConfig | IFormGroupConfig): string[] {
    const watchedFields = new Set<string>();

    // Если это IFormGroupConfig (single nested group), обрабатываем его fields рекурсивно
    if ('type' in fields && fields.type === FormFieldType.GROUP) {
      if (fields.fields && typeof fields.fields === 'object') {
        // fields.fields может быть как IFormGroupFieldConfig так и IFormGroupConfig
        if ('type' in fields.fields && fields.fields.type === FormFieldType.GROUP) {
          // Это вложенная группа
          const nestedFields = this.collectWatchedFields(fields.fields);
          nestedFields.forEach(field => watchedFields.add(field));
        } else {
          // Это объект с полями
          const nestedFields = this.collectWatchedFields(fields.fields as IFormGroupFieldConfig);
          nestedFields.forEach(field => watchedFields.add(field));
        }
      }
      return Array.from(watchedFields);
    }

    // Обрабатываем как IFormGroupFieldConfig
    const fieldConfigs = fields as IFormGroupFieldConfig;

    Object.values(fieldConfigs).forEach(fieldConfig => {
      if (fieldConfig?.hideCondition) {
        fieldConfig.hideCondition.forEach(condition => {
          if (condition.fieldName) {
            watchedFields.add(condition.fieldName);
          }
        });
      }

      if (fieldConfig?.type === FormFieldType.GROUP && fieldConfig.fields) {
        const nestedFields = this.collectWatchedFields(fieldConfig.fields);
        nestedFields.forEach(field => watchedFields.add(field));
      }
    });

    return Array.from(watchedFields);
  }
}
